import React from 'react';
import type { LegalBlock } from '../../legal/types';
import { renderInline } from './renderInline';

interface LegalBlocksProps {
  blocks: LegalBlock[];
}

/**
 * Renders a legal body as real HTML: paragraphs, unordered lists, h3
 * subheadings and tables. Tables sit in a horizontally scrolling wrapper so
 * the three-column lawful-basis table survives a phone without the copy
 * shrinking below body size.
 */
export default function LegalBlocks({ blocks }: LegalBlocksProps) {
  return (
    <>
      {blocks.map((block, index) => {
        switch (block.type) {
          case 'p':
            return <p key={index}>{renderInline(block.text)}</p>;
          case 'h3':
            return (
              <h3 key={index} className="mk-legal__h3">
                {renderInline(block.text)}
              </h3>
            );
          case 'ul':
            return (
              <ul key={index}>
                {block.items.map((item, i) => (
                  <li key={i}>{renderInline(item)}</li>
                ))}
              </ul>
            );
          case 'table': {
            const hasHeader = block.columns.some((c) => c.trim() !== '');
            return (
              <div key={index} className="mk-legal__table-wrap">
                <table className="mk-legal__table">
                  {hasHeader && (
                    <thead>
                      <tr>
                        {block.columns.map((column, i) => (
                          <th key={i} scope="col">
                            {renderInline(column)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                  )}
                  <tbody>
                    {block.rows.map((row, r) => (
                      <tr key={r}>
                        {row.map((cell, c) =>
                          !hasHeader && c === 0 ? (
                            <th key={c} scope="row">
                              {renderInline(cell)}
                            </th>
                          ) : (
                            <td key={c}>{renderInline(cell)}</td>
                          ),
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }
          default:
            return null;
        }
      })}
    </>
  );
}
